import { LowerCasePipe, NgTemplateOutlet } from '@angular/common';
import { Component, computed } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { VisualComponent } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/visual/visual.component';
import { AnalyticsDetailsTableComponent } from '@cta-analytics-dialog/deliverable-analytics-content/analytics-details-table/analytics-details-table.component';
import { DeliverableFindingsStore } from '@cta-analytics-dialog/deliverable-analytics-content/deliverable-findings.store';
import { TimelineGraphComponent } from '@cta-analytics-dialog/deliverable-analytics-content/graphs/timeline-graph/timeline-graph.component';
import { ITimelineGraphInputData } from '@cta-analytics-dialog/deliverable-analytics-content/graphs/timeline-graph/timeline-graph.interface';
import { EDeliverableType } from '@enums/campaign.enum';
import { IVisualAnalytics } from '@interfaces/deliverable-analytics.interface';
import { TranslateModule } from '@ngx-translate/core';
import { DotsSpinnerComponent } from '@shared/components/dots-spinner/dots-spinner.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { StatusBadgeComponent } from '@shared/components/status-badge/status-badge.component';
import { DurationUtilitiesService } from '@utilities/duration-utilities.service';
import { MultiMediaPostAnalyticsBaseDirective } from '../multi-media-post-analytics-base/multi-media-post-analytics-base.directive';
import { PostMediaViewerComponent } from '../post-media-viewer/post-media-viewer.component';
import { IVisualPostAnalyticsTableData } from './visual-post-analytics.interface';

@Component({
  imports: [
    NgTemplateOutlet,
    TranslateModule,
    TimelineGraphComponent,
    AnalyticsDetailsTableComponent,
    MatTableModule,
    MatSortModule,
    MatButtonModule,
    StatusBadgeComponent,
    PostMediaViewerComponent,
    VisualComponent,
    DotsSpinnerComponent,
    IconComponent,
    LowerCasePipe,
  ],
  providers: [DeliverableFindingsStore],
  selector: 'app-visual-post-analytics',
  templateUrl: './visual-post-analytics.component.html',
})
export class VisualPostAnalyticsComponent extends MultiMediaPostAnalyticsBaseDirective<
  IVisualPostAnalyticsTableData,
  IVisualAnalytics
> {
  public override readonly deliverableType = EDeliverableType.visual;
  public override readonly displayedColumns: string[] = ['timestamp', 'status', 'actions'];
  public override readonly dataSource: MatTableDataSource<IVisualPostAnalyticsTableData> = new MatTableDataSource();

  public override readonly currentDeliverable = computed(() => {
    const deliverableId = this.deliverableId();
    const executionAnalytics = this.executionAnalyticsWithFindings();

    if (deliverableId && executionAnalytics) {
      return executionAnalytics?.ctaAnalytics?.visuals?.find((v) => v.deliverableId === deliverableId);
    }

    return null;
  });

  public readonly currentMedia = computed(() => {
    const { src } = this.videoConfiguration();
    const mediaList = this.media();
    let found = mediaList.find((m) => m.url === src);

    if (!found && mediaList.length > 0) {
      [found] = mediaList;
    }

    return found;
  });

  public override readonly kpiCardData = null;

  protected override setCounts(): void {
    return;
  }

  protected override setKeywordList(): void {
    return;
  }

  protected override getKeywords(): string[] {
    return [];
  }

  protected override prepareChartAndTableData(): void {
    const tableData: IVisualPostAnalyticsTableData[] = [];
    const dotGraphData: ITimelineGraphInputData[] = [];
    const currentVisualAnalytics = this.currentDeliverable();
    const removedIds = this.manualFindingStatusStore?.serverRemovedIds() ?? new Set<string>();
    const findings = (currentVisualAnalytics?.findings || [])
      .filter((f) => !removedIds.has(f.id))
      .sort((a, b) => (a.mediaIndex ?? 0) - (b.mediaIndex ?? 0));
    let imageFindingTimeOffset = 0;
    let previousMediaIndex = 0;
    let previousMediaIsVideo = this.media()[previousMediaIndex]?.isVideo ?? false;

    findings.forEach((entry) => {
      const { id } = entry;
      const isImage = !this.media()[entry.mediaIndex ?? 0]?.isVideo;
      const videoTime = entry.videoTime ?? 0;
      const mediaIndex = entry.mediaIndex ?? 0;
      const aggregatedVideoTime = this.calculateAggregatedVideoTime(mediaIndex, videoTime);
      const mediaIndexChanged = mediaIndex !== previousMediaIndex;

      if (mediaIndexChanged && previousMediaIsVideo) {
        imageFindingTimeOffset = 0;
        previousMediaIndex = mediaIndex;
      }

      previousMediaIsVideo = !isImage;

      const xAxisValue = isImage ? imageFindingTimeOffset++ : aggregatedVideoTime;

      dotGraphData.push({
        id,
        isImage,
        mediaIndex,
        status: currentVisualAnalytics?.status!,
        videoTime,
        xAxisValue,
      });

      tableData.push({
        aggregatedVideoTime,
        findingParentId: entry.parentId,
        id,
        mediaIndex,
        status: currentVisualAnalytics?.status ?? '',
        timestamp: DurationUtilitiesService.convertSecondsToVideoTime(aggregatedVideoTime, true),
        videoTime,
      });
    });

    tableData.sort((a, b) => a.aggregatedVideoTime - b.aggregatedVideoTime);

    this.dataSource.data = tableData;
    this.dataCount.set(tableData.length);
    this.dotGraphData.set([...dotGraphData].sort((a, b) => a.xAxisValue - b.xAxisValue));
  }
}
